import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Pause, Play, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, inr } from "@/lib/api";

const EMPTY = { code: "", type: "percent", value: "", min_amount: "", label: "" };

export default function AdminPromos() {
  const { user } = useAuth();
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/promos").then((r) => setPromos(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/promos", { ...form, value: Number(form.value), min_amount: Number(form.min_amount || 0) });
      toast.success(`${form.code.toUpperCase()} created`);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const toggle = async (p) => { await api.patch(`/admin/promos/${p.code}`, { active: !p.active }); load(); };
  const remove = async (p) => { if (!window.confirm(`Delete ${p.code}?`)) return; await api.delete(`/admin/promos/${p.code}`); toast.success("Deleted"); load(); };

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="mx-auto max-w-5xl px-5 py-12 sm:px-8" data-testid="admin-promos-page">
        <p className="overline">Admin</p>
        <h1 className="mt-2 font-serif text-4xl tracking-tight">Promo codes</h1>
        <form onSubmit={create} className="paper mt-8 grid gap-4 rounded-lg p-5 sm:grid-cols-6" data-testid="promo-create-form">
          <div className="sm:col-span-2"><Label>Code</Label><Input required value={form.code} onChange={set("code")} placeholder="DIWALI20" data-testid="promo-form-code" className="mt-1 h-10 bg-white uppercase" /></div>
          <div><Label>Type</Label>
            <select value={form.type} onChange={set("type")} data-testid="promo-form-type" className="mt-1 h-10 w-full rounded-md border border-[#E8E2D9] bg-white px-2 text-sm">
              <option value="percent">% off</option><option value="flat">₹ off</option>
            </select></div>
          <div><Label>Value</Label><Input required type="number" min="1" value={form.value} onChange={set("value")} data-testid="promo-form-value" className="mt-1 h-10 bg-white" /></div>
          <div><Label>Min order ₹</Label><Input type="number" min="0" value={form.min_amount} onChange={set("min_amount")} data-testid="promo-form-min" className="mt-1 h-10 bg-white" /></div>
          <div className="flex items-end"><button disabled={busy} data-testid="promo-form-submit" className="btn-primary h-10 w-full !py-0"><Plus className="h-4 w-4" />Create</button></div>
        </form>
        <div className="paper mt-8 overflow-x-auto rounded-lg">
          <table className="w-full text-sm" data-testid="promo-table">
            <thead className="text-left text-xs uppercase tracking-wider text-[#8C8498]"><tr>{["Code", "Discount", "Min order", "Used", "Status", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
            <tbody>
              {promos.map((p) => (
                <tr key={p.code} className="border-t border-[#E8E2D9]" data-testid={`promo-row-${p.code}`}>
                  <td className="px-4 py-3 font-mono font-semibold">{p.code}</td>
                  <td className="px-4 py-3">{p.type === "percent" ? `${p.value}%` : inr(p.value)} <span className="text-[#8C8498]">· {p.label}</span></td>
                  <td className="px-4 py-3">{p.min_amount ? inr(p.min_amount) : "—"}</td>
                  <td className="px-4 py-3" data-testid={`promo-used-${p.code}`}>{p.used || 0}</td>
                  <td className="px-4 py-3"><span data-testid={`promo-status-${p.code}`} className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${p.active ? "bg-[#F0F5F1] text-[#4F7A5C]" : "bg-[#F5F0E8] text-[#8C8498]"}`}>{p.active ? "Active" : "Paused"}</span></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => toggle(p)} data-testid={`promo-toggle-${p.code}`} className="btn-ghost !px-3 !py-1.5 !text-xs">{p.active ? <><Pause className="h-3.5 w-3.5" />Pause</> : <><Play className="h-3.5 w-3.5" />Resume</>}</button>
                    <button onClick={() => remove(p)} data-testid={`promo-delete-${p.code}`} className="ml-2 rounded-full p-2 text-[#8C8498] hover:text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
              {!promos.length && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8C8498]">No promo codes yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
